import Immutable from 'immutable';
import moment from 'moment';
import TypedDate from 'sharedreact/data/TypedDate';
import TypedFloat from 'sharedreact/data/TypedFloat';
import TypedInteger from 'sharedreact/data/TypedInteger';

const PredicateRuleOperator = {
  CHOICE: 'CHOICE',
  STRING_CONTAINS: 'STRING_CONTAINS',
  STRING_BEGINS_WITH: 'STRING_BEGINS_WITH',
  STRING_ENDS_WITH: 'STRING_ENDS_WITH',
  STRING_IS: 'STRING_IS',
  STRING_IS_EMPTY: 'STRING_IS_EMPTY',
  AND: 'AND',
  OR: 'OR',
  NOT: 'NOT',
  DATE_ANY: 'DATE_ANY',
  DATE_WITHIN_LAST: 'DATE_WITHIN_LAST',
  DATE_OLDER_THAN: 'DATE_OLDER_THAN',
  DATE_EXACTLY: 'DATE_EXACTLY',
  DATE_BEFORE: 'DATE_BEFORE',
  DATE_AFTER: 'DATE_AFTER',
  DATE_BETWEEN: 'DATE_BETWEEN',
  DATE_TODAY: 'DATE_TODAY',
  DATE_YESTERDAY: 'DATE_YESTERDAY',
  DATE_SINCE_YESTERDAY: 'DATE_SINCE_YESTERDAY',
  DATE_THIS_WEEK: 'DATE_THIS_WEEK',
  DATE_THIS_MONTH: 'DATE_THIS_MONTH',
  DATE_THIS_YEAR: 'DATE_THIS_YEAR',
  DATE_WITHIN_NEXT: 'DATE_WITHIN_NEXT',
  DATE_BEFORE_TODAY: 'DATE_BEFORE_TODAY',
  DATE_AFTER_TODAY: 'DATE_AFTER_TODAY',
  DATE_EMPTY: 'DATE_EMPTY'
};

type ValueType = string | TypedInteger | TypedFloat | TypedDate | null;

const Content: {
  attribute: string | null,
  value: ValueType,
  operator: string,
  date?: moment.Moment,
  endDate?: moment.Moment,
  duration: number,
  unit: string
} = {
  attribute: null,
  value: null,
  operator: '',
  date: undefined,
  endDate: undefined,
  duration: 0,
  unit: ''
};

class PredicateRule extends Immutable.Record(Content) {
  static CHOICE: string;
  static STRING_CONTAINS: string;
  static STRING_BEGINS_WITH: string;
  static STRING_ENDS_WITH: string;
  static STRING_IS: string;
  static STRING_IS_EMPTY: string;
  static DATE_ANY: string;
  static DATE_WITHIN_LAST: string;
  static DATE_OLDER_THAN: string;
  static DATE_EXACTLY: string;
  static DATE_BEFORE: string;
  static DATE_AFTER: string;
  static DATE_BETWEEN: string;
  static DATE_TODAY: string;
  static DATE_YESTERDAY: string;
  static DATE_SINCE_YESTERDAY: string;
  static DATE_THIS_WEEK: string;
  static DATE_THIS_MONTH: string;
  static DATE_THIS_YEAR: string;
  static DATE_WITHIN_NEXT: string;
  static DATE_BEFORE_TODAY: string;
  static DATE_AFTER_TODAY: string;
  static DATE_EMPTY: string;

  static split (inSearchString: string): string[] {
    let orTokens: string[] = [];
    let token: string = '';
    let escaped: boolean = false;
    let quoted: boolean = false;

    let index: number;
    for (index = 0; index < inSearchString.length; ++index) {
      const c = inSearchString.charAt(index);

      if (c === '"') {
        if (escaped) {
          token = token.substring(0, token.length - 1);
          token = token.concat(c);
        } else {
          if (quoted) {
            orTokens.push(token);
            token = '';
            quoted = false;
          } else {
            let s: string = token.trim();
            if (s && s !== '') {
              orTokens.push(s);
            }
            token = '';
            quoted = true;
          }
        }
      } else if (c === ';') {
        if (quoted) {
          token = token.concat(c);
        } else {
          let s: string = token.trim();
          if (s && s !== '') {
            orTokens.push(s);
          }
          token = '';
        }
      } else if (c === '\\') {
        if (escaped) {
          token = token.substring(0, token.length - 1);
          token = token.concat(c);
        } else {
          token = token.concat(c);
        }
      } else {
        token = token.concat(c);
      }

      // Set the escape flag.
      if (c === '\\') {
        if (escaped) {
          escaped = false;
        } else {
          escaped = true;
        }
      } else {
        escaped = false;
      }
    }

    // Add the last token.
    if (token.length > 0) {
      let s = token;
      if (!quoted) {
        s = s.trim();
      }

      if (s && s !== '') {
        orTokens.push(s);
      }
    }

    return orTokens;
  }

  static split2 (inSearchString: string): string[][] {
    let orTokens: string[][] = [];
    let andTokens: string[] = [];
    let token: string = '';
    let escaped: boolean = false;
    let quoted: boolean = false;

    let index: number;
    for (index = 0; index < inSearchString.length; ++index) {
      const c = inSearchString.charAt(index);
      if (c.trim() === '') { // whitespace character
        if (quoted) {
          token = token.concat(c);
        } else {
          // Start a new and token.
          let s: string = token.trim();
          if (s && s !== '') {
            andTokens.push(s);
          }
          token = '';
        }
      } else if (c === '"') {
        if (escaped) {
          token = token.substring(0, token.length - 1);
          token = token.concat(c);
        } else {
          if (quoted) {
            andTokens.push(token);
            token = '';
            quoted = false;
          } else {
            let s: string = token.trim();
            if (s && s !== '') {
              andTokens.push(s);
            }
            token = '';
            quoted = true;
          }
        }
      } else if (c === ';') {
        if (quoted) {
          token = token.concat(c);
        } else {
          let s: string = token.trim();
          if (s && s !== '') {
            andTokens.push(s);
          }
          token = '';

          if (andTokens.length > 0) {
            orTokens.push(andTokens);
            andTokens = [];
          }
        }
      } else if (c === '\\') {
        if (escaped) {
          token = token.substring(0, token.length - 1);
          token = token.concat(c);
        } else {
          token = token.concat(c);
        }
      } else {
        token = token.concat(c);
      }

      // Set the escape flag.
      if (c === '\\') {
        if (escaped) {
          escaped = false;
        } else {
          escaped = true;
        }
      } else {
        escaped = false;
      }
    }

    // Add the last token.
    if (token.length > 0) {
      let s = token;
      if (!quoted) {
        s = s.trim();
      }

      if (s && s !== '') {
        andTokens.push(s);
      }
    }

    // Add the pending and tokens.
    if (andTokens.length > 0) {
      orTokens.push(andTokens);
    }

    return orTokens;
  }

  static createRule (attribute: string | null, value: ValueType, operator: string): PredicateRule {
    return new PredicateRule({attribute: attribute, value: value, operator: operator});
  }

  static createAND (): PredicateRule {
    return new PredicateRule({attribute: null, value: '', operator: PredicateRuleOperator.AND});
  }

  static createOR (): PredicateRule {
    return new PredicateRule({attribute: null, value: '', operator: PredicateRuleOperator.OR});
  }

  static createNOT (): PredicateRule {
    return new PredicateRule({attribute: null, value: '', operator: PredicateRuleOperator.NOT});
  }

  static isAND (rule: PredicateRule): boolean {
    return (rule.operator === PredicateRuleOperator.AND);
  }

  static isOR (rule: PredicateRule): boolean {
    return (rule.operator === PredicateRuleOperator.OR);
  }

  static isNOT (rule: PredicateRule): boolean {
    return (rule.operator === PredicateRuleOperator.NOT);
  }
}

PredicateRule.CHOICE = PredicateRuleOperator.CHOICE;
PredicateRule.STRING_CONTAINS = PredicateRuleOperator.STRING_CONTAINS;
PredicateRule.STRING_BEGINS_WITH = PredicateRuleOperator.STRING_BEGINS_WITH;
PredicateRule.STRING_ENDS_WITH = PredicateRuleOperator.STRING_ENDS_WITH;
PredicateRule.STRING_IS = PredicateRuleOperator.STRING_IS;
PredicateRule.STRING_IS_EMPTY = PredicateRuleOperator.STRING_IS_EMPTY;
PredicateRule.DATE_ANY = PredicateRuleOperator.DATE_ANY,
PredicateRule.DATE_WITHIN_LAST = PredicateRuleOperator.DATE_WITHIN_LAST,
PredicateRule.DATE_OLDER_THAN = PredicateRuleOperator.DATE_OLDER_THAN,
PredicateRule.DATE_EXACTLY = PredicateRuleOperator.DATE_EXACTLY,
PredicateRule.DATE_BEFORE = PredicateRuleOperator.DATE_BEFORE,
PredicateRule.DATE_AFTER = PredicateRuleOperator.DATE_AFTER,
PredicateRule.DATE_BETWEEN = PredicateRuleOperator.DATE_BETWEEN,
PredicateRule.DATE_TODAY = PredicateRuleOperator.DATE_TODAY,
PredicateRule.DATE_YESTERDAY = PredicateRuleOperator.DATE_YESTERDAY,
PredicateRule.DATE_SINCE_YESTERDAY = PredicateRuleOperator.DATE_SINCE_YESTERDAY,
PredicateRule.DATE_THIS_WEEK = PredicateRuleOperator.DATE_THIS_WEEK,
PredicateRule.DATE_THIS_MONTH = PredicateRuleOperator.DATE_THIS_MONTH,
PredicateRule.DATE_THIS_YEAR = PredicateRuleOperator.DATE_THIS_YEAR,
PredicateRule.DATE_WITHIN_NEXT = PredicateRuleOperator.DATE_WITHIN_NEXT,
PredicateRule.DATE_BEFORE_TODAY = PredicateRuleOperator.DATE_BEFORE_TODAY,
PredicateRule.DATE_AFTER_TODAY = PredicateRuleOperator.DATE_AFTER_TODAY,
PredicateRule.DATE_EMPTY = PredicateRuleOperator.DATE_EMPTY

export default PredicateRule;
